import { Download, File, FileSpreadsheet, FileText, Loader2, Presentation } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toDisplayableOriginalFilename, type MessageAttachmentDto } from "@multi-chat/shared";
import { cn } from "../../../lib/utils.js";
import { AttachmentCardShell } from "../AttachmentCardShell/AttachmentCardShell.js";
import {
  attachmentDownloadFilename,
  downloadAttachmentFromSource,
} from "../attachment-media-actions.js";
import { downloadActionI18nKey } from "../attachment-download-state.js";
import {
  documentCardKind,
  documentExtension,
  documentKindAccentClass,
  formatHumanFileSize,
} from "../document-card.js";
import { MessageDocumentPreview } from "../MessageDocumentPreview/MessageDocumentPreview.js";
import { useAttachmentDownload } from "../use-attachment-download.js";
import { useInboxAttachmentUrl } from "../use-inbox-attachment-url.js";

function DocumentKindIcon({
  kind,
}: {
  kind: ReturnType<typeof documentCardKind>;
}): React.JSX.Element {
  const className = "size-5 shrink-0";
  switch (kind) {
    case "pdf":
    case "text":
      return <FileText className={className} aria-hidden="true" />;
    case "word":
      return <FileText className={className} aria-hidden="true" />;
    case "excel":
      return <FileSpreadsheet className={className} aria-hidden="true" />;
    case "powerpoint":
      return <Presentation className={className} aria-hidden="true" />;
    case "generic":
      return <File className={className} aria-hidden="true" />;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

export function MessageDocument({
  organizationId,
  attachment,
}: {
  organizationId: number;
  attachment: MessageAttachmentDto;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { failed, urlResolving, ensureUrl } = useInboxAttachmentUrl(organizationId, attachment);
  const download = useAttachmentDownload();
  const [previewOpen, setPreviewOpen] = useState(false);

  const displayFileName = toDisplayableOriginalFilename(attachment.fileName);
  const kind = documentCardKind(displayFileName, attachment.mimeType);
  const extension = documentExtension(displayFileName, attachment.mimeType);
  const sizeLabel = formatHumanFileSize(attachment.fileSizeBytes);
  const fileName = displayFileName || t("inbox.messageType.DOCUMENT");

  const handleDownload = useCallback(async () => {
    await download.run(async () => {
      const downloadName = attachmentDownloadFilename(
        displayFileName,
        attachment.mimeType,
        "document",
      );
      try {
        await downloadAttachmentFromSource(
          { kind: "stored", organizationId, attachmentId: attachment.attachmentId },
          downloadName,
        );
      } catch {
        const url = await ensureUrl();
        if (!url) {
          throw new Error("download_failed");
        }
        await downloadAttachmentFromSource({ kind: "url", url }, downloadName);
      }
    });
  }, [
    attachment.attachmentId,
    attachment.mimeType,
    displayFileName,
    download,
    ensureUrl,
    organizationId,
  ]);

  if (failed) {
    return (
      <p className="m-0 text-[0.8125rem] text-ink-subtle">{t("inbox.media.unavailable")}</p>
    );
  }

  return (
    <>
      <AttachmentCardShell className="flex-wrap">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-start gap-2.5 border-0 bg-transparent p-0 text-start"
          onClick={() => setPreviewOpen(true)}
          aria-label={t("inbox.document.previewOpen")}
        >
          <span
            className={cn(
              "flex size-10 shrink-0 items-center justify-center rounded-lg",
              documentKindAccentClass(kind),
            )}
          >
            <DocumentKindIcon kind={kind} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="m-0 block truncate text-[0.8125rem] font-medium text-ink">
              {fileName}
            </span>
            <span className="m-0 mt-0.5 block text-[11px] uppercase tracking-wide text-ink-subtle">
              {[extension, sizeLabel].filter(Boolean).join(" · ") || t("inbox.messageType.DOCUMENT")}
            </span>
          </span>
        </button>
        <button
          type="button"
          className="mt-0.5 inline-flex shrink-0 items-center gap-1 self-center rounded px-0 text-[0.75rem] font-medium text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:no-underline"
          disabled={urlResolving || download.disabled}
          onClick={() => void handleDownload()}
        >
          {download.status === "downloading" ? (
            <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <Download className="size-3.5" aria-hidden="true" />
          )}
          {t(downloadActionI18nKey(download.status))}
        </button>
      </AttachmentCardShell>
      <MessageDocumentPreview
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        organizationId={organizationId}
        attachment={attachment}
        onDownload={() => void handleDownload()}
        downloadStatus={download.status}
        downloadDisabled={urlResolving || download.disabled}
      />
    </>
  );
}
